import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import VideoCard from '@/components/feature/VideoCard';
import FilterBar, { type FilterState } from '@/pages/category/components/FilterBar';
import Pagination from '@/pages/category/components/Pagination';
import { categoryMap } from '@/mocks/categories';
import { getVideosByCategory } from '@/utils/videoUtils';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 8;

const DEFAULT_FILTER: FilterState = { year: 'all', region: 'all', sort: 'default' };

export default function Category() {
  const { type = '' } = useParams();
  const info = categoryMap[type];

  const [filter, setFilter] = useState<FilterState>(DEFAULT_FILTER);
  const [page, setPage] = useState(1);

  const baseList = useMemo(() => getVideosByCategory(type), [type]);

  const years = useMemo(
    () => Array.from(new Set(baseList.map((v) => v.year))).sort((a, b) => b - a),
    [baseList],
  );
  const regions = useMemo(
    () => Array.from(new Set(baseList.map((v) => v.region))),
    [baseList],
  );

  const filtered = useMemo(() => {
    let list = [...baseList];
    if (filter.year !== 'all') list = list.filter((v) => String(v.year) === filter.year);
    if (filter.region !== 'all') list = list.filter((v) => v.region === filter.region);
    if (filter.sort === 'latest') {
      list.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    } else if (filter.sort === 'hot') {
      list.sort((a, b) => b.plays - a.plays);
    }
    return list;
  }, [baseList, filter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const shown = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  usePageMeta({
    title: info ? `${info.name} - 在线视频` : '视频分类 - 在线视频',
    description: info
      ? `${info.desc}在线视频${info.name}频道，共收录 ${baseList.length} 部优质内容，支持按年份、地区筛选与排序。`
      : '在线视频分类频道，按内容类型浏览精彩视频。',
    keywords: info ? `${info.name},在线视频,${info.name}在线观看,${info.en}` : '在线视频分类',
  });

  const handleFilterChange = (next: FilterState) => {
    setFilter(next);
    setPage(1);
  };

  if (!info) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-20 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
          <h1 className="mt-4 font-display text-2xl font-bold text-foreground-950">未找到该分类</h1>
          <p className="mt-2 text-sm text-foreground-500">你访问的分类不存在，去其他分类看看吧。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6 pt-6">
        <nav className="flex items-center gap-1.5 text-sm text-foreground-500 flex-wrap" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-700 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span>分类</span>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900 font-medium">{info.name}</span>
        </nav>

        <div className="mt-5 flex items-center gap-4">
          <span className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
            <i className={`${info.icon} text-primary-700 text-2xl`}></i>
          </span>
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground-950">
              {info.name}
            </h1>
            <p className="mt-1 text-sm text-foreground-500">{info.desc}</p>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          <aside className="w-full lg:w-64 shrink-0">
            <FilterBar
              years={years}
              regions={regions}
              value={filter}
              onChange={handleFilterChange}
              onReset={() => handleFilterChange(DEFAULT_FILTER)}
            />
            <div className="hidden lg:block mt-5 rounded-lg bg-background-100 border border-background-200 p-5">
              <h3 className="font-display text-sm font-bold text-foreground-950 mb-3">分类导航</h3>
              <div className="space-y-1">
                {Object.values(categoryMap).map((c) => (
                  <Link
                    key={c.key}
                    to={`/category/${c.key}`}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                      c.key === type
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'text-foreground-700 hover:bg-background-200'
                    }`}
                  >
                    <i className={c.icon}></i>
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-foreground-500">
                共找到 <span className="font-semibold text-primary-700">{filtered.length}</span> 个视频
              </p>
            </div>

            {shown.length === 0 ? (
              <div className="rounded-lg bg-background-100 border border-background-200 py-20 text-center">
                <i className="ri-search-eye-line text-5xl text-foreground-300"></i>
                <h2 className="mt-4 font-display text-lg font-bold text-foreground-950">
                  暂无该分类视频，换个条件试试
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  当前筛选条件下没有匹配的内容，你可以重置筛选条件重新查找。
                </p>
                <button
                  type="button"
                  onClick={() => handleFilterChange(DEFAULT_FILTER)}
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  重置筛选条件
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                  {shown.map((video) => (
                    <VideoCard key={video.id} video={video} showDescription />
                  ))}
                </div>
                <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
              </>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}